import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Edit3, Save, Undo, Redo, ArrowLeft, LayoutDashboard, Presentation, X, Plus, Trash2, ChevronLeft, ChevronRight, MoreVertical, Menu, Check, Settings, RotateCcw, Home } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useBrand } from '@/context/BrandContext';
import { BrandSection } from '../sections/BrandSection';
import { BrandSettingsPanel } from '../editor/BrandSettingsPanel';
import { SectionCustomizer } from '../editor/SectionCustomizer';
import { useWorkspace } from '@/context/WorkspaceContext';
import { useAuth } from '@/context/AuthContext';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { brandService } from '@/lib/brandService';
import { toast } from 'sonner';

interface BrandPreviewProps {
  isGlobalDarkMode: boolean;
}

export const BrandPreview: React.FC<BrandPreviewProps> = ({ isGlobalDarkMode }) => {
  const { state, dispatch } = useBrand();
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [showGrid, setShowGrid] = useState(false);
  const [isPresentMode, setIsPresentMode] = useState(false);
  const [showBrandSettings, setShowBrandSettings] = useState(false);
  const [showSectionCustomizer, setShowSectionCustomizer] = useState(false);
  const [isToolbarMinimized, setIsToolbarMinimized] = useState(false);
  const [showExitButton, setShowExitButton] = useState(false);
  const [showToolbar, setShowToolbar] = useState(true);
  const [showSliderControls, setShowSliderControls] = useState(true);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const navigate = useNavigate();

  // Speculative preloading of adjacent sections
  useEffect(() => {
    if (!state.sections || state.sections.length === 0) return;

    const preloadSection = (index: number) => {
      if (index < 0 || index >= state.sections.length) return;
      const section = state.sections[index];
      if (section && section.content) {
        Object.values(section.content).forEach(value => {
          if (typeof value === 'string' && (value.startsWith('http') || value.startsWith('/'))) {
            if (value.match(/\.(jpeg|jpg|gif|png|webp|svg)/i)) {
              const img = new Image();
              img.src = value;
            }
          }
        });
      }
    };

    // Preload next and previous sections
    preloadSection(state.currentSection + 1);
    preloadSection(state.currentSection - 1);
  }, [state.currentSection, state.sections]);

  // Mouse position tracking for presentation mode and toolbar/slider visibility
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const viewportHeight = window.innerHeight;
      const topThreshold = viewportHeight * 0.2; // Top 20% of viewport
      const bottomThreshold = viewportHeight * 0.8; // Bottom 20% of viewport (80% from top)

      if (isPresentMode) {
        // Exit button visibility in presentation mode
        if (e.clientY <= topThreshold) {
          setShowExitButton(true);
        } else {
          setShowExitButton(false);
        }
      } else {
        // Toolbar visibility (top 20%)
        if (e.clientY <= topThreshold) {
          setShowToolbar(true);
        } else {
          setShowToolbar(false);
        }

        // Slider controls visibility (bottom 20%)
        if (e.clientY >= bottomThreshold) {
          setShowSliderControls(true);
        } else {
          setShowSliderControls(false);
        }
      }
    };

    // Add mouse move listener
    window.addEventListener('mousemove', handleMouseMove);

    // Clean up listener
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isPresentMode]);

  // Reset visibility states when entering/exiting presentation mode
  useEffect(() => {
    if (isPresentMode) {
      setShowExitButton(false);
      setShowToolbar(false);
      setShowSliderControls(false);
    } else {
      setShowExitButton(false);
      setShowToolbar(true);
      setShowSliderControls(true);
    }
  }, [isPresentMode]);

  // Keyboard navigation - ONLY when NOT in edit mode and NOT when brand settings panel is open
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Disable all shortcuts when brand settings panel is open
      if (showBrandSettings) {
        return;
      }

      // Number key navigation for table of contents (1-9)
      if (e.key >= '1' && e.key <= '9' && !isEditing) {
        e.preventDefault();

        // Define the section IDs in table of contents order
        const tocSectionIds = [
          'introduction',     // 1 - Brand Introduction
          'brand-story',      // 2 - Brand Story
          'mission-vision',   // 3 - Mission & Vision
          'brand-logo',       // 4 - Brand Logo
          'do-dont',          // 5 - Do & Don't
          'typography',       // 6 - Typography
          'colors',           // 7 - Color Palette
          'brand-action',     // 8 - Brand In Action
          'thank-you'         // 9 - Thank You
        ];

        const sectionNumber = parseInt(e.key);
        const targetSectionId = tocSectionIds[sectionNumber - 1]; // Convert to 0-based index

        if (targetSectionId) {
          // Find the index of the target section in the current sections array
          const targetIndex = state.sections.findIndex((section: any) => section.id === targetSectionId);

          if (targetIndex !== -1) {
            dispatch({
              type: 'SET_CURRENT_SECTION',
              payload: targetIndex
            });
          }
        }
      }
      // H key to toggle toolbar visibility
      else if (e.key === 'h' && !isEditing && !isPresentMode) {
        e.preventDefault();
        setIsToolbarMinimized(!isToolbarMinimized);
      }
      // G key to toggle grid mode
      else if (e.key === 'g' && !isEditing && !isPresentMode) {
        e.preventDefault();
        setShowGrid(!showGrid);
      }
      // Cmd/Ctrl + B to open brand settings
      else if (e.key === 'b' && (e.metaKey || e.ctrlKey) && !isEditing && !isPresentMode) {
        e.preventDefault();
        setShowBrandSettings(true);
      }
      // Cmd/Ctrl + S to save brand
      else if (e.key === 's' && (e.metaKey || e.ctrlKey) && !isEditing) {
        e.preventDefault();
        handleSave();
      }
      // P key to toggle present mode
      else if (e.key === 'p' && !isEditing) {
        e.preventDefault();
        handlePresentMode();
      }
      // CRITICAL FIX: Only allow keyboard navigation when NOT editing
      else if (e.key === 'ArrowLeft' && state.currentSection > 0 && !isEditing) {
        dispatch({
          type: 'SET_CURRENT_SECTION',
          payload: state.currentSection - 1
        });
      } else if (e.key === 'ArrowRight' && state.currentSection < state.sections.length - 1 && !isEditing) {
        dispatch({
          type: 'SET_CURRENT_SECTION',
          payload: state.currentSection + 1
        });
      } else if (e.key === 'Escape' && isPresentMode) {
        e.preventDefault();
        setIsPresentMode(false);
      } else if (e.key === 'e' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setIsEditing(!isEditing);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [state.currentSection, state.sections.length, isPresentMode, isEditing, isToolbarMinimized, showGrid, showBrandSettings, dispatch]);

  const handleSave = async () => {
    if (!currentWorkspace || !user || !state.brandData) {
      toast.error('Please ensure you are logged in and have a workspace selected');
      return;
    }

    setSaveStatus('saving');

    try {
      if (state.currentBrandId) {
        // Update existing brand
        const updatedBrand = await brandService.updateBrand(
          state.currentBrandId,
          state.brandData,
          state.sections
        );

        // Update the brand in the saved brands list
        const updatedBrands = state.savedBrands.map((brand: any) =>
          brand.id === state.currentBrandId
            ? {
              id: updatedBrand.id,
              name: updatedBrand.name,
              status: updatedBrand.status,
              createdAt: updatedBrand.created_at,
              brandData: state.brandData,
              sections: state.sections,
              updatedAt: updatedBrand.updated_at,
            }
            : brand
        );
        dispatch({ type: 'LOAD_SAVED_BRANDS', payload: updatedBrands });

        setSaveStatus('saved');
        toast.success('Brand updated successfully');

        setTimeout(() => {
          setSaveStatus('idle');
        }, 2000);
      } else {
        // Create new brand
        const newBrand = await brandService.createBrand(
          currentWorkspace.id,
          state.brandData,
          state.sections,
          user.id
        );

        // Set the brand ID for future updates
        dispatch({ type: 'SET_CURRENT_BRAND_ID', payload: newBrand.id });

        // Add to saved brands list
        const brandToSave = {
          id: newBrand.id,
          name: newBrand.name,
          status: newBrand.status,
          createdAt: newBrand.created_at,
          brandData: state.brandData,
          sections: state.sections,
        };
        dispatch({ type: 'SAVE_BRAND', payload: brandToSave });

        toast.success('Brand created successfully');
        navigate('/home');
      }
    } catch (error) {
      console.error('Error saving brand:', error);
      setSaveStatus('error');
      toast.error('Failed to save brand');
      setTimeout(() => {
        setSaveStatus('idle');
      }, 2000);
    }
  };

  const handleUndo = () => {
    dispatch({ type: 'UNDO' });
  };

  const handleRedo = () => {
    dispatch({ type: 'REDO' });
  };

  const handlePresentMode = () => {
    setIsPresentMode(!isPresentMode);
    setIsEditing(false);
    setShowGrid(false);
  };

  // Reset to first section
  const handleResetToFirst = () => {
    dispatch({ type: 'SET_CURRENT_SECTION', payload: 0 });
  };

  // Move slide functions - Only left and right
  const moveSlide = (fromIndex: number, direction: 'left' | 'right') => {
    let toIndex: number;

    if (direction === 'left') {
      toIndex = fromIndex - 1;
    } else {
      toIndex = fromIndex + 1;
    }

    // Check bounds
    if (toIndex < 0 || toIndex >= state.sections.length) return;

    const newSections = [...state.sections];
    const [movedSection] = newSections.splice(fromIndex, 1);
    newSections.splice(toIndex, 0, movedSection);

    dispatch({ type: 'SET_SECTIONS', payload: newSections });
  };

  // Delete a section
  const handleDeleteSection = (index: number) => {
    if (state.sections.length <= 1) return; // Don't delete if it's the last section

    const newSections = state.sections.filter((_: any, i: number) => i !== index);
    dispatch({ type: 'SET_SECTIONS', payload: newSections });

    // Adjust current section if needed
    if (state.currentSection >= newSections.length) {
      dispatch({ type: 'SET_CURRENT_SECTION', payload: newSections.length - 1 });
    }
  };

  // Add a new section
  const handleAddSection = () => {
    const newSection = {
      id: `new-section-${Date.now()}`,
      title: 'New Section',
      content: {
        title: 'New Section',
        description: 'Add your content here...'
      },
      styles: {
        backgroundColor: '#FFFFFF',
        textColor: '#1F2937',
        fontFamily: 'Inter',
        fontSize: '24px',
        padding: '60px'
      }
    };

    const newSections = [...state.sections, newSection];
    dispatch({ type: 'SET_SECTIONS', payload: newSections });
  };

  // Check if brand is already saved
  const isBrandSaved = state.savedBrands.some(
    (brand: any) => brand.brandData?.name === state.brandData?.name
  );

  // Check if we're on the last section
  const isLastSection = state.currentSection === state.sections.length - 1;

  if (state.isLoading) {
    return null;
  }

  if (!state.sections.length) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className={`text-2xl font-bold mb-4 ${isGlobalDarkMode ? 'text-white' : 'text-gray-900'
            }`}>No Brand Data</h2>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-workspace-accent hover:bg-workspace-accent-hover text-white rounded-md transition-colors"
          >
            Start Creating
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-hidden">
      {/* Save Status Notification */}
      <AnimatePresence>
        {saveStatus !== 'idle' && (
          <motion.div
            initial={{ opacity: 0, y: -50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -50, scale: 0.9 }}
            className="fixed top-6 right-6 z-50"
          >
            <div className={`px-4 py-3 rounded-lg shadow-lg border backdrop-blur-lg flex items-center space-x-3 ${saveStatus === 'saving'
                ? 'bg-card border-border text-foreground'
                : saveStatus === 'saved'
                  ? 'bg-green-500/20 border-green-500/30 text-green-600 dark:text-green-400'
                  : 'bg-red-500/20 border-red-500/30 text-red-600 dark:text-red-400'
              }`}>
              {saveStatus === 'saving' && (
                <>
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  <span className="text-sm font-medium">Saving changes...</span>
                </>
              )}
              {saveStatus === 'saved' && (
                <>
                  <Check className="w-4 h-4" />
                  <span className="text-sm font-medium">Changes saved successfully!</span>
                </>
              )}
              {saveStatus === 'error' && (
                <>
                  <X className="w-4 h-4" />
                  <span className="text-sm font-medium">Failed to save changes</span>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Top Center Toolbar - Mouse Triggered Fade */}
      {!isPresentMode && (
        <div className="fixed top-6 left-1/2 transform -translate-x-1/2 z-50">
          <AnimatePresence>
            {showToolbar && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{
                  opacity: 1,
                  y: 0,
                  transition: {
                    type: "spring",
                    stiffness: 400,
                    damping: 25,
                    duration: 0.4
                  }
                }}
                exit={{
                  opacity: 0,
                  y: -20,
                  transition: {
                    duration: 0.3,
                    ease: "easeInOut"
                  }
                }}
              >
                <AnimatePresence mode="wait">
                  {isToolbarMinimized ? (
                    // Minimized Hamburger Menu
                    <motion.button
                      key="hamburger"
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      onClick={() => setIsToolbarMinimized(false)}
                      className="p-3 rounded-md border backdrop-blur-lg shadow-lg transition-colors bg-card border-border text-muted-foreground hover:text-foreground hover:bg-background shadow-sm"
                      title="Show Toolbar"
                    >
                      <Menu className="w-5 h-5" />
                    </motion.button>
                  ) : (
                    // Full Toolbar
                    <motion.div
                      key="toolbar"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="flex items-center space-x-3 p-3 rounded-md border backdrop-blur-lg shadow-lg bg-card border-border"
                    >
                      {/* Hamburger Menu Button */}
                      <button
                        onClick={() => setIsToolbarMinimized(true)}
                        className="p-2 rounded-md transition-colors text-muted-foreground hover:text-foreground hover:bg-accent"
                        title="Hide Toolbar"
                      >
                        <Menu className="w-4 h-4" />
                      </button>

                      {/* Separator */}
                      <div className="w-px h-6 bg-border" />

                      {/* Back to Dashboard Button - HIDDEN IN EDIT MODE */}
                      {!isEditing && (
                        <button
                          onClick={() => navigate('/brand')}
                          className="p-2 rounded-md transition-colors text-muted-foreground hover:text-foreground hover:bg-accent"
                          title="Back to Dashboard"
                        >
                          <Home className="w-4 h-4" />
                        </button>
                      )}

                      {/* Grid View Button - HIDDEN IN EDIT MODE */}
                      {!isEditing && (
                        <button
                          onClick={() => setShowGrid(!showGrid)}
                          className={`p-2 rounded-md transition-colors ${showGrid
                              ? 'bg-workspace-accent text-white hover:bg-workspace-accent-hover'
                              : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                            }`}
                          title="Grid View"
                        >
                          <LayoutDashboard className="w-4 h-4" />
                        </button>
                      )}

                      {/* Brand Settings Button - HIDDEN IN EDIT MODE */}
                      {!isEditing && (
                        <button
                          onClick={() => setShowBrandSettings(true)}
                          className="p-2 rounded-md transition-colors text-muted-foreground hover:text-foreground hover:bg-accent"
                          title="Brand Settings"
                        >
                          <Settings className="w-4 h-4" />
                        </button>
                      )}

                      {/* Section Customizer Button - ONLY IN EDIT MODE */}
                      {isEditing && (
                        <button
                          onClick={() => setShowSectionCustomizer(true)}
                          className="p-2 rounded-md transition-colors bg-workspace-accent text-white hover:bg-workspace-accent-hover"
                          title="Customize Section"
                        >
                          <Settings className="w-4 h-4" />
                        </button>
                      )}

                      {/* Undo/Redo only visible in edit mode */}
                      {isEditing && (
                        <>
                          <button
                            onClick={handleUndo}
                            disabled={state.historyIndex <= 0}
                            className="p-2 rounded-md transition-colors disabled:opacity-50 text-muted-foreground hover:text-foreground hover:bg-accent"
                            title="Undo"
                          >
                            <Undo className="w-4 h-4" />
                          </button>

                          <button
                            onClick={handleRedo}
                            disabled={state.historyIndex >= state.history.length - 1}
                            className="p-2 rounded-md transition-colors disabled:opacity-50 text-muted-foreground hover:text-foreground hover:bg-accent"
                            title="Redo"
                          >
                            <Redo className="w-4 h-4" />
                          </button>
                        </>
                      )}

                      {/* Edit Mode Button */}
                      <button
                        onClick={() => setIsEditing(!isEditing)}
                        className={`p-2 rounded-md transition-colors ${isEditing
                            ? 'bg-green-500 text-white hover:bg-green-600'
                            : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                          }`}
                        title={isEditing ? 'Exit Edit Mode (Cmd/Ctrl+E)' : 'Edit Mode (Cmd/Ctrl+E)'}
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>

                      {/* Save Button - Dynamic behavior and visual feedback */}
                      <button
                        onClick={handleSave}
                        disabled={saveStatus === 'saving'}
                        className={`p-2 rounded-md transition-all duration-200 flex items-center space-x-1 ${saveStatus === 'saving'
                            ? 'bg-workspace-accent/70 text-white cursor-not-allowed'
                            : saveStatus === 'saved'
                              ? 'bg-green-500 text-white hover:bg-green-600'
                              : 'bg-workspace-accent text-white hover:bg-workspace-accent-hover'
                          }`}
                        title={
                          isBrandSaved
                            ? "Save changes to existing brand"
                            : "Save as new brand (will redirect to dashboard)"
                        }
                      >
                        {saveStatus === 'saving' ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : saveStatus === 'saved' ? (
                          <Check className="w-4 h-4" />
                        ) : (
                          <Save className="w-4 h-4" />
                        )}
                      </button>

                      {/* Separator - HIDDEN IN EDIT MODE */}
                      {!isEditing && (
                        <div className="w-px h-6 bg-border" />
                      )}

                      {/* Presentation Mode Button - Far Right - HIDDEN IN EDIT MODE */}
                      {!isEditing && (
                        <button
                          onClick={handlePresentMode}
                          className="p-2 bg-red-500 text-white rounded-md transition-colors hover:bg-red-600"
                          title="Present Mode"
                        >
                          <Presentation className="w-4 h-4" />
                        </button>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Presentation Mode Exit Button - Top Center - Mouse Triggered - BLACK COLOR & SMALLER SIZE */}
      {isPresentMode && (
        <div className="fixed top-6 left-1/2 transform -translate-x-1/2 z-50">
          <AnimatePresence>
            {showExitButton && (
              <motion.button
                initial={{ opacity: 0, y: -20, scale: 0.8 }}
                animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1,
                  transition: {
                    type: "spring",
                    stiffness: 400,
                    damping: 25,
                    duration: 0.3
                  }
                }}
                exit={{
                  opacity: 0,
                  y: -20,
                  scale: 0.8,
                  transition: {
                    duration: 0.2,
                    ease: "easeInOut"
                  }
                }}
                onClick={handlePresentMode}
                className="p-2 bg-card/95 text-foreground rounded-md transition-colors hover:bg-accent backdrop-blur-sm shadow-lg border border-border"
                title="Exit Present Mode"
              >
                <X className="w-4 h-4" />
              </motion.button>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Content */}
      <div className="h-screen">
        {showGrid ? (
          // Grid View - 3x3 with Left/Right Arrow Controls Only
          <div className="p-8 h-full overflow-y-auto">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-3 gap-8 mb-8">
                {state.sections.map((section: any, index: number) => (
                  <GridCard
                    key={section.id}
                    section={section}
                    index={index}
                    onMove={moveSlide}
                    onDelete={handleDeleteSection}
                    onSelect={() => {
                      setShowGrid(false);
                      dispatch({ type: 'SET_CURRENT_SECTION', payload: index });
                    }}
                    canDelete={state.sections.length > 1}
                    totalSections={state.sections.length}
                  />
                ))}

                {/* Add New Slide Card - Same size as other slides */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{
                    opacity: 1,
                    scale: 1,
                    transition: {
                      delay: state.sections.length * 0.08,
                      type: "spring",
                      stiffness: 500,
                      damping: 30
                    }
                  }}
                  className="aspect-video rounded-lg border-2 border-dashed cursor-pointer transition-all duration-200 flex items-center justify-center border-border bg-muted/50 hover:bg-muted"
                  onClick={handleAddSection}
                >
                  <div className="text-center">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-2 bg-muted">
                      <Plus className="w-6 h-6 text-muted-foreground" />
                    </div>
                    <p className="text-sm font-medium text-muted-foreground">
                      Add New Slide
                    </p>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        ) : (
          // Full Screen View
          <div className="relative h-full">
            <BrandSection
              section={state.sections[state.currentSection]}
              isEditing={isEditing}
              isGlobalDarkMode={isGlobalDarkMode}
            />

            {/* Section Navigation - Bottom - Mouse Triggered Fade - Hidden in Present and Edit Mode */}
            {!isPresentMode && !isEditing && (
              <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-40">
                <AnimatePresence>
                  {showSliderControls && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{
                        opacity: 1,
                        y: 0,
                        transition: {
                          type: "spring",
                          stiffness: 400,
                          damping: 25,
                          duration: 0.4
                        }
                      }}
                      exit={{
                        opacity: 0,
                        y: 20,
                        transition: {
                          duration: 0.3,
                          ease: "easeInOut"
                        }
                      }}
                      className="flex items-center space-x-2 rounded-md px-4 py-2 border backdrop-blur-lg shadow-lg bg-card border-border"
                    >
                      <button
                        onClick={() => dispatch({
                          type: 'SET_CURRENT_SECTION',
                          payload: Math.max(0, state.currentSection - 1)
                        })}
                        disabled={state.currentSection === 0}
                        className="p-1.5 transition-colors disabled:opacity-50 text-muted-foreground hover:text-foreground hover:bg-accent rounded-md"
                      >
                        <ArrowLeft className="w-3 h-3" />
                      </button>

                      <span className="font-medium text-sm px-2 text-foreground">
                        {state.currentSection + 1} / {state.sections.length}
                      </span>

                      <button
                        onClick={() => dispatch({
                          type: 'SET_CURRENT_SECTION',
                          payload: Math.min(state.sections.length - 1, state.currentSection + 1)
                        })}
                        disabled={state.currentSection === state.sections.length - 1}
                        className="p-1.5 transition-colors disabled:opacity-50 text-muted-foreground hover:text-foreground hover:bg-accent rounded-md"
                      >
                        <ArrowLeft className="w-3 h-3 rotate-180" />
                      </button>

                      {/* Reset Icon - Only show when on last section */}
                      {isLastSection && (
                        <>
                          {/* Separator */}
                          <div className="w-px h-4 mx-1 bg-border" />

                          {/* Reset Button */}
                          <button
                            onClick={handleResetToFirst}
                            className="p-1.5 transition-colors text-muted-foreground hover:text-foreground hover:bg-accent rounded-md"
                            title="Reset to first section"
                          >
                            <RotateCcw className="w-3 h-3" />
                          </button>
                        </>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Brand Settings Panel */}
      {showBrandSettings && (
        <BrandSettingsPanel
          isOpen={showBrandSettings}
          onClose={() => setShowBrandSettings(false)}
          isGlobalDarkMode={isGlobalDarkMode}
        />
      )}

      {/* Section Customizer */}
      {showSectionCustomizer && (
        <SectionCustomizer
          isOpen={showSectionCustomizer}
          onClose={() => setShowSectionCustomizer(false)}
          isGlobalDarkMode={isGlobalDarkMode}
          sectionIndex={state.currentSection}
        />
      )}
    </div>
  );
};

// Separate GridCard component for better organization
interface GridCardProps {
  section: any;
  index: number;
  onMove: (fromIndex: number, direction: 'left' | 'right') => void;
  onDelete: (index: number) => void;
  onSelect: () => void;
  canDelete: boolean;
  totalSections: number;
}

const GridCard: React.FC<GridCardProps> = ({
  section,
  index,
  onMove,
  onDelete,
  onSelect,
  canDelete,
  totalSections
}) => {
  const [showMenu, setShowMenu] = useState(false);

  return (
    <motion.div
      className="relative group"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{
        opacity: 1,
        scale: 1,
        transition: {
          delay: index * 0.08,
          type: "spring",
          stiffness: 500,
          damping: 30
        }
      }}
      layout
      layoutId={section.id}
    >
      <div
        className="aspect-video rounded-lg overflow-hidden border-2 transition-all duration-200 cursor-pointer bg-muted border-border hover:border-workspace-accent"
        onClick={onSelect}
      >
        <div
          className="w-full h-full flex items-center justify-center relative"
          style={{
            backgroundColor: section.customization?.backgroundType === 'color'
              ? section.customization.backgroundColor
              : section.styles.backgroundColor,
            backgroundImage: section.customization?.backgroundType === 'image' && section.customization.backgroundImage
              ? `url(${section.customization.backgroundImage})`
              : section.customization?.backgroundType === 'gradient'
                ? `linear-gradient(${section.customization.gradientAngle || 135}deg, ${section.customization.gradientFrom || '#8b5cf6'}, ${section.customization.gradientTo || '#06b6d4'})`
                : 'none',
            backgroundPosition: section.customization?.backgroundPosition || 'center',
            backgroundSize: section.customization?.backgroundSize || 'cover',
            backgroundRepeat: section.customization?.backgroundRepeat || 'no-repeat',
            color: section.styles.textColor
          }}
        >
          <h3 className="text-lg font-bold text-center px-4 pointer-events-none">
            {section.title}
          </h3>

          {/* Slide Number */}
          <div className="absolute top-2 left-2 bg-card/90 text-foreground px-2 py-1 rounded text-xs pointer-events-none border border-border">
            {index + 1}
          </div>

          {/* 3-Dot Menu Button */}
          <div className="absolute top-2 right-2">
            <button
              className="bg-header-control/90 backdrop-blur-sm text-foreground p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-20 border border-sidebar-border shadow-lg"
              onClick={(e) => {
                e.stopPropagation();
                setShowMenu(!showMenu);
              }}
              title="Options"
            >
              <MoreVertical className="w-3 h-3" />
            </button>

            {/* Dropdown Menu */}
            {showMenu && (
              <>
                {/* Backdrop to close menu */}
                <div
                  className="fixed inset-0 z-30"
                  onClick={() => setShowMenu(false)}
                />

                {/* Menu */}
                <div className="absolute top-8 right-0 z-40 rounded-md border shadow-lg min-w-[120px] bg-card border-border">
                  {canDelete && (
                    <button
                      className="w-full px-3 py-2 text-left text-sm transition-colors flex items-center space-x-2 text-red-600 dark:text-red-400 hover:bg-accent"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(index);
                        setShowMenu(false);
                      }}
                    >
                      <Trash2 className="w-3 h-3" />
                      <span>Delete</span>
                    </button>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Left/Right Arrow Controls - Fixed positioning to prevent movement on hover */}
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
            {/* Left Arrow - Fixed position with no hover transform */}
            {index > 0 && (
              <button
                className="absolute bg-header-control/90 backdrop-blur-sm text-foreground p-1.5 rounded-full transition-colors pointer-events-auto border border-sidebar-border shadow-lg hover:bg-background shadow-sm"
                style={{
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onMove(index, 'left');
                }}
                title="Move Left"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
            )}

            {/* Right Arrow - Fixed position with no hover transform */}
            {index < totalSections - 1 && (
              <button
                className="absolute bg-header-control/90 backdrop-blur-sm text-foreground p-1.5 rounded-full transition-colors pointer-events-auto border border-sidebar-border shadow-lg hover:bg-background shadow-sm"
                style={{
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onMove(index, 'right');
                }}
                title="Move Right"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};